import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail, downloadExport } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader, Confirm, EmptyState } from "../components/Shared";
import Money from "../components/Money";
import { fmtFCFA, fmtDate, ROLES } from "../lib/format";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { FileText, Plus, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { TableSkeleton } from "../components/Skeletons";
import ExportMenu from "../components/ExportMenu";

export default function FinancialStatements() {
  const { isRole } = useAuth();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ project_id: "", period: "", remarks: "" });
  const [editing, setEditing] = useState(null);
  const canCreate = isRole(ROLES.CEO, ROLES.FM);
  const canManage = isRole(ROLES.CEO);

  const qc = useQueryClient();
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: () => api.get("/projects").then((r) => r.data) });
  const { data: rows = [], isLoading: rowsLoading } = useQuery({ queryKey: ["financial-statements"], queryFn: () => api.get("/financial-statements").then((r) => r.data) });
  const load = () => qc.invalidateQueries();

  const generate = async () => {
    if (!form.project_id) return;
    try {
      await api.post("/financial-statements", form);
      toast.success("Statement generated");
      setOpen(false); setForm({ project_id: "", period: "", remarks: "" }); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const saveEdit = async () => {
    try {
      await api.put(`/financial-statements/${editing.id}`, { period: editing.period, remarks: editing.remarks });
      toast.success("Statement updated"); setEditing(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (r) => {
    try { await api.delete(`/financial-statements/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Financial Statements" subtitle="Project balances, income, expenditure and remarks">
        <Button variant="outline" data-testid="company-workbook-btn" onClick={() => downloadExport("/export/company-workbook", "stems_company_workbook.xlsx")}>
          <FileText className="h-4 w-4 mr-2" /> Full Workbook
        </Button>
        <ExportMenu dataset="financial-statements" filename="stems_financial_statements" pdfPath="/export/financial-summary/pdf" pdfLabel="Company Summary" testId="statements-export" />
        {canCreate && <Button data-testid="generate-statement-button" onClick={() => setOpen(true)} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]"><Plus className="h-4 w-4 mr-2" /> Generate Statement</Button>}
      </PageHeader>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {rowsLoading ? <TableSkeleton cols={7} /> : rows.length === 0 ? <EmptyState text="No financial statements yet." /> : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Project</TableHead><TableHead>Period</TableHead>
              <TableHead className="text-right">Income</TableHead><TableHead className="text-right">Expenditure</TableHead>
              <TableHead className="text-right">Balance</TableHead><TableHead>Generated</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`statement-row-${r.id}`}>
                  <TableCell className="font-medium text-slate-800">{r.project_name}</TableCell>
                  <TableCell className="text-slate-600">{r.period || "—"}</TableCell>
                  <TableCell className="text-right font-mono-num text-emerald-700">{fmtFCFA(r.total_income)}</TableCell>
                  <TableCell className="text-right font-mono-num">{fmtFCFA((r.total_expenditure || 0) + (r.total_payments || 0))}</TableCell>
                  <TableCell className="text-right"><Money value={r.balance} showIcon={false} /></TableCell>
                  <TableCell className="text-slate-500">{fmtDate(r.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="stems-actions">
                      <Button size="icon" variant="ghost" onClick={() => window.print()} data-testid={`print-statement-${r.id}`}><FileText className="h-4 w-4" /></Button>
                      {canManage && <>
                        <Button size="icon" variant="ghost" onClick={() => setEditing({ ...r })} data-testid={`edit-statement-${r.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Confirm onConfirm={() => remove(r)} title="Delete statement?" description="Historical statements are best voided rather than deleted."
                          trigger={<Button size="icon" variant="ghost" className="text-red-600" data-testid={`delete-statement-${r.id}`}><Trash2 className="h-4 w-4" /></Button>} />
                      </>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Generate dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Generate Financial Statement</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-sm">Project</Label>
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="statement-project"><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-sm">Reporting Period</Label><Input data-testid="statement-period" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} placeholder="e.g. Q1 2026" /></div>
            <div className="space-y-1.5"><Label className="text-sm">Remarks</Label><Textarea data-testid="statement-remarks" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={generate} disabled={!form.project_id} data-testid="statement-submit" className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">Generate</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Statement</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="space-y-1.5"><Label className="text-sm">Reporting Period</Label><Input value={editing.period || ""} onChange={(e) => setEditing({ ...editing, period: e.target.value })} /></div>
              <div className="space-y-1.5"><Label className="text-sm">Remarks</Label><Textarea value={editing.remarks || ""} onChange={(e) => setEditing({ ...editing, remarks: e.target.value })} /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
